
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import Container from '@/components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { createModel } from '@/utils/component'
import {
  PaymentSlipHistoryModel,
  PaymentSlipHistoryPageParamModel
} from '@/model/paymentSlip/paymentSlipHistoryModel'
import { getPaymentSlipHistoryPageList } from '@/apis/paymentSlip/paymentSlip'
import { ElMessage } from 'element-plus'
import { exportVoucherBatchTemplate, exportVoucherTemplate } from '@/apis/paymentSlip/paymentSlipHistory'
import { formatDate, formatThousands, toMaximumTimeOfDay } from '@/utils'
import dayjs from 'dayjs'
import SimpleTableForIcon from '@/components/table/simple/SimpleTableForIcon.vue'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'PaymentSlipHistoryList',
  components: {
    SimpleTableForIcon,
    SearchForm,
    Container
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const tableRef = ref()
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'payee', label: t('pay.payee'), component: 'el-input', type: 'text', model: '' },
      { prop: 'dateRange', label: t('pay.paymentTime'), component: 'el-date-picker', type: 'daterange', model: [] },
      {
        prop: 'status',
        label: t('pay.status'),
        placeholder: t('Acceptance.all'),
        component: 'el-select',
        type: 'text',
        model: '',
        options: [
          { label: t('pay.all'), value: null },
          { label: t('pay.toBeConfirmed'), value: 0 },
          { label: t('pay.hasPayment'), value: 6 },
          { label: t('pay.Cancelled'), value: 9 },
          { label: t('pay.paymentError'), value: -2 }
        ]
      },
      {
        prop: 'paymentType',
        label: t('pay.paymentSlipType'),
        placeholder: t('Acceptance.all'),
        component: 'el-select',
        type: 'number',
        model: null,
        options: [
          { label: t('pay.all'), value: null },
          { label: t('paymentSlipTypes.cash'), value: 1 },
          { label: t('paymentSlipTypes.vitro'), value: 2 },
          { label: t('paymentSlipTypes.acceptance'), value: 3 }
        ]
      }
    ]
    const formatPaymentSlipType = (data: PaymentSlipHistoryModel | null) => {
      if (data !== null) {
        switch (data.status) {
          case 0:
          case 1: // 现汇支付
            return '现汇支付'
          case 2: // 体外支付
            return '体外支付'
          case 3: // 承兑支付
            return '承兑支付'
          case 9: // 取消
            return '取消'
          default:
            return ''
        }
      } else {
        return ''
      }
    }
    const formatStatus = (data: PaymentSlipHistoryModel | null) => {
      if (data !== null) {
        if (data.status === 9) {
          return '已取消'
        }
        switch (data.submitStatus) {
          case 0:
            return '待确认'
          case 1:
            return '已支付'
          case 2: // 支付失败
            return '支付失败'
          default:
            return ''
        }
      } else {
        return ''
      }
    }
    const downloadButtonStatus = (data: PaymentSlipHistoryModel | null) => {
      if (data !== null) {
        switch (data.status) {
          case 1: // 现汇支付
            return data.submitStatus !== 1
          case 2: // 体外支付
            return false
          case 0: // '待确认'
          case 3: // 承兑支付
          case 9: // 取消
            return true
          default:
            return true
        }
      } else {
        return true
      }
    }
    const formatIconTooltip = (tip: string | null) => {
      if (tip !== null && tip.includes('-')) {
        const tipSplit = tip.split('-')
        const tipCode = tipSplit[0]
        const tipMessage = tipSplit.filter((t, i) => i > 0).join('')
        return `${tipCode}：${tipMessage}`
      }
      return `${tip}：`
    }
    const approveAcceptanceTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'companyBankName', label: t('pay.PaymentBank') },
      { prop: 'vendorName', label: t('pay.payee') },
      { prop: 'vendorNumber', label: t('supplier.supplierCode') },
      { prop: 'bankName', label: t('pay.ReceivingBank') },
      {
        prop: 'amount',
        label: t('pay.PaymentAmount'),
        formatter: (row: any) => formatThousands(row.amount),
        align: 'right'
      },
      { prop: 'currency', label: t('pay.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      {
        prop: 'createdAt',
        label: t('pay.paymentTime'),
        formatter: (row: any) => row.status === 9 ? '' : formatDate(row.createdAt)
      },
      { prop: 'status', label: t('pay.paymentSlipType'), formatter: (row: any) => formatPaymentSlipType(row) },
      {
        prop: 'submitStatus',
        label: t('pay.status'),
        formatter: (row: any) => formatStatus(row),
        icon: (row: any) => row.submitStatus === 2 ? 'error' : row.submitStatus === 0 ? 'warning' : 'success',
        showIcon: (row: any) => row.submitStatus === 2 || row.submitStatus === 1 || (row.status === 0 && row.submitStatus === 0 && row.submitComment != null),
        iconTooltip: 'submitComment',
        formatterIconTooltip: (tip: string) => formatIconTooltip(tip)
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<PaymentSlipHistoryModel>(),
      columnStruct: approveAcceptanceTableStruct,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true

        let startDate = null as string | null
        let endDate = null as string | null
        if (dataMap.searchFormModel.dateRange && dataMap.searchFormModel.dateRange.length > 1) {
          startDate = dataMap.searchFormModel.dateRange[0] ? dayjs(dataMap.searchFormModel.dateRange[0])
            .format('YYYY-MM-DD HH:mm:ss') : null
          endDate = dataMap.searchFormModel.dateRange[1] ? dayjs(toMaximumTimeOfDay(dataMap.searchFormModel.dateRange[1]))
            .format('YYYY-MM-DD HH:mm:ss') : null
        }

        const params: PaymentSlipHistoryPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          status: dataMap.searchFormModel.status,
          paymentType: dataMap.searchFormModel.paymentType,
          payee: dataMap.searchFormModel.payee,
          paymentDateBegin: startDate,
          paymentDateEnd: endDate,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getPaymentSlipHistoryPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })
    const viewDetail = async(row: any) => {
      switch (row.status) {
        case 9:
        case 0:
        case 1:
        case 2:
          await router.push({ path: `cashDetail/${row.id}` })
          break
        case 3:
          await router.push({ path: `acceptanceDetail/${row.id}` })
          break
        default:
          break
      }
    }
    const downloadCertificate = async(row: PaymentSlipHistoryModel) => {
      if (row.paymentVoucher) {
        const formData = new FormData() as any
        formData.append('voucher', row.paymentVoucher)
        const urlBlob = await exportVoucherTemplate(formData)
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = row.paymentVoucher
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取文件失败',
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('pay.paymentVoucherIsNull'),
          type: 'error',
          duration: 2000
        })
      }
    }
    const BatchDownloadCertificateClick = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        const formData = new FormData() as any
        formData.append('vouchers', tableRef.value.getSelectionRows().map((row: any) => row.paymentVoucher))
        const urlBlob = await exportVoucherBatchTemplate(formData)
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = 'Vouchers_Download.zip'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取文件失败',
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      tableRef,
      viewDetail,
      downloadButtonStatus,
      downloadCertificate,
      BatchDownloadCertificateClick,
      ...toRefs(dataMap)
    }
  }
})
